import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Calendar, MapPin, Trophy, ExternalLink, IndianRupee, Users } from "lucide-react";
import SEO from "@/components/SEO";
import { RaceJoinButton, RaceJoiners, RacePlanButton } from "@/components/RaceJoin";

export default function Races() {
  const [races, setRaces] = useState([]);

  useEffect(() => {
    api.get("/races").then((r) => setRaces(r.data)).catch(() => {});
  }, []);

  const replace = (updated) => setRaces((rs) => rs.map((r) => (r.race_id === updated.race_id ? updated : r)));

  const now = Date.now();
  const isPast = (r) => new Date(r.date).getTime() < now - 6 * 60 * 60 * 1000;
  const upcoming = races.filter((r) => !isPast(r));
  const past = races.filter(isPast).sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="races-page">
      <SEO
        title="Upcoming Races"
        path="/races"
        description="Marathons, half marathons and ultras across India — curated by OSCR. Registration links, distances, fees and dates for every race we recommend."
      />

      <div className="editorial-lift">
        <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">
          THE STARTING LINE
        </p>
        <h1 className="font-editorial text-5xl sm:text-6xl lg:text-7xl text-white mt-2 tracking-tight leading-[0.9]">
          Upcoming <span className="text-orange-500">races.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          Marathons, half marathons, trail runs and ultras across India — curated for OSCR runners.
          Tap any race for registration details, distance options and organiser info.
        </p>
      </div>

      {/* UPCOMING */}
      <section className="mt-14" data-testid="races-upcoming-section">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl text-white">Upcoming</h2>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
            {upcoming.length} live
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {upcoming.length === 0 && (
            <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm md:col-span-2 lg:col-span-3">
              No races posted yet. Check back soon — Sumit curates the best each month.
            </div>
          )}
          {upcoming.map((r) => <RaceCard key={r.race_id} r={r} onChange={replace} />)}
        </div>
      </section>

      {/* PAST */}
      {past.length > 0 && (
        <section className="mt-16" data-testid="races-past-section">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-display text-2xl text-white">Recently Completed</h2>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
              {past.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {past.map((r) => <RaceCard key={r.race_id} r={r} past />)}
          </div>
        </section>
      )}
    </div>
  );
}

function RaceCard({ r, past, onChange }) {
  return (
    <article
      data-testid={`race-card-${r.race_id}`}
      className={`hairline overflow-hidden card-editorial flex flex-col ${
        past ? "bg-zinc-950 border-zinc-800 opacity-90" : "bg-zinc-900"
      }`}
    >
      {r.image && (
        <div className="aspect-[16/10] bg-zinc-800 overflow-hidden relative">
          <img
            src={r.image}
            alt={r.title}
            loading="lazy"
            className={`w-full h-full object-cover transition duration-700 ${
              past ? "grayscale hover:grayscale-0" : "hover:scale-105"
            }`}
          />
          {past && (
            <span className="absolute top-3 left-3 bg-black/70 backdrop-blur text-zinc-300 font-mono text-[10px] tracking-[0.25em] uppercase px-2 py-1">
              Completed
            </span>
          )}
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <p className={`font-mono text-[10px] tracking-[0.25em] uppercase flex items-center gap-1.5 ${past ? "text-zinc-500" : "text-orange-500"}`}>
          <Calendar size={12} />
          {new Date(r.date).toLocaleString(undefined, { dateStyle: "medium" })}
        </p>
        <h3 className="font-display text-2xl text-white mt-2 tracking-tight leading-tight">
          {r.title}
        </h3>

        <div className="mt-3 space-y-1.5">
          <p className="text-xs text-zinc-400 flex items-center gap-1.5">
            <MapPin size={12} className="text-orange-500" /> {r.location}
          </p>
          {r.organiser && (
            <p className="text-xs text-zinc-400 flex items-center gap-1.5">
              <Users size={12} className="text-orange-500" /> {r.organiser}
            </p>
          )}
          {r.distances && (
            <p className="text-xs text-zinc-400 flex items-center gap-1.5">
              <Trophy size={12} className="text-orange-500" /> {r.distances}
            </p>
          )}
          {r.entry_fee && (
            <p className="text-xs text-zinc-400 flex items-center gap-1.5">
              <IndianRupee size={12} className="text-orange-500" /> {r.entry_fee}
            </p>
          )}
        </div>

        {r.description && (
          <p className="text-sm text-zinc-400 mt-4 leading-relaxed line-clamp-4">{r.description}</p>
        )}

        {r.pitch && !past && (
          <p data-testid={`race-pitch-${r.race_id}`} className="mt-4 border-l-2 border-orange-600 pl-3 text-sm text-zinc-300 italic">
            {r.pitch}
          </p>
        )}

        {!past && (
          <div className="mt-auto pt-5 space-y-3">
            <RaceJoiners race={r} max={4} />
            <div className="flex flex-wrap gap-2">
              <RaceJoinButton race={r} onChange={onChange} size="sm" />
              <RacePlanButton race={r} onChange={onChange} size="sm" />
              {r.registration_url && (
                <a
                  href={r.registration_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`race-register-${r.race_id}`}
                  className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-display tracking-wider text-xs px-3 py-2 transition"
                >
                  REGISTER <ExternalLink size={13} />
                </a>
              )}
            </div>
          </div>
        )}
        {past && (r.joiners_count || 0) > 0 && (
          <p className="mt-auto pt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
            {r.joiners_count} OSCR {r.joiners_count === 1 ? "runner" : "runners"} ran this
          </p>
        )}
      </div>
    </article>
  );
}
